<template>
  <div>
    <v-card>
      <v-card-title class="primary white--text">
        <span class="headline">{{ $t("ridesListPage.pageTitle") }}</span>
        <v-spacer></v-spacer>
        <v-btn dark icon @click="showFilters = !showFilters">
          <v-icon>{{ showFilters ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-expand-transition>
        <v-card-text v-show="showFilters" class="filters-section pa-4">
          <v-layout row wrap>
            <!-- Row 1: Date Filters -->
            <v-flex xs12 md4>
              <v-menu
                v-model="fromDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="filters.from_date"
                    :label='$t("ridesListPage.fromDate")'
                    prepend-icon="event"
                    readonly
                    v-on="on"
                    clearable
                    outlined
                    dense
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="filters.from_date"
                  @input="fromDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-flex>

            <v-flex xs12 md4>
              <v-menu
                v-model="toDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="filters.to_date"
                    :label='$t("ridesListPage.toDate")'
                    prepend-icon="event"
                    readonly
                    v-on="on"
                    clearable
                    outlined
                    dense
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="filters.to_date"
                  @input="toDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-flex>

            <v-flex xs12 md4>
              <v-text-field
                v-model="filters.order_id"
                :label='$t("ridesListPage.orderId")'
                clearable
                outlined
                dense
                prepend-icon="tag"
              ></v-text-field>
            </v-flex>


            <!-- Row 2: Status, Time Type, and empty space -->
            <v-flex xs12 md4>
              <v-select
                v-model="filters.statuses"
                :items="statusOptions"
                :label='$t("ridesListPage.status")'
                multiple
                chips
                clearable
                item-value="value"
                item-text="text"
                outlined
                dense
                prepend-icon="info"
              ></v-select>
            </v-flex>

            <v-flex xs12 md4>
              <v-select
                v-model="filters.time"
                :items="timeOptions"
                :label='$t("ridesListPage.orderType")'
                clearable
                item-value="value"
                item-text="text"
                outlined
                dense
                prepend-icon="schedule"
              ></v-select>
            </v-flex>


            <!-- Row 4: Multi-select Filters -->
            <v-flex xs12 md4>
              <rides-driver-filter
                v-model="filters.captain_ids"
                :label='$t("ridesListPage.drivers")'
              ></rides-driver-filter>
            </v-flex>

            <v-flex xs12 md4>
              <rides-passenger-filter
                v-model="filters.passenger_ids"
                :label='$t("ridesListPage.passengers")'
              ></rides-passenger-filter>
            </v-flex>

            <v-flex xs12 md4>
              <rides-service-filter
                v-model="filters.service_ids"
                :label='$t("ridesListPage.services")'
              ></rides-service-filter>
            </v-flex>

            <!-- Action Buttons -->
            <v-flex xs12 class="mt-2">
              <v-btn color="primary" @click="applyFilters" class="me-2">
                <v-icon left>search</v-icon>
                {{ $t("ridesListPage.applyFilters") }}
              </v-btn>
              <v-btn color="secondary" @click="resetFilters" class="me-2">
                <v-icon left>refresh</v-icon>
                {{ $t("ridesListPage.reset") }}
              </v-btn>
              <v-btn color="success" @click="exportExcel" :loading="exporting">
                <v-icon left>cloud_download</v-icon>
                {{ $t("ridesListPage.exportExcel") }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-expand-transition>
    </v-card>

    <v-card class="mt-3">
      <DataTable
        :url="api.ACTIONS.ADVANCED_SEARCH"
        ref="ridesTable"
        perPage="25"
        :enable-add="false"
        :enable-edit="false"
        :enable-delete="false"
        :formatter="formatter"
        :actions-extra-main="actions"
        :fields-hidden-main="fieldsHidden"
        :titles="titles"
        :key="filterKey"
        :initial-url-search-params="searchParams"
        sortBy="id"
        :descending="true"
      />
    </v-card>
  </div>
</template>

<script>
import DataTable from "@/components/monaco/DataTable";
import RidesDriverFilter from "./components/RidesDriverFilter";
import RidesPassengerFilter from "./components/RidesPassengerFilter";
import RidesServiceFilter from "./components/RidesServiceFilter";
import moment from "moment";
import { companyToday, formatCompanyTime } from "@/util/companyTime";

export default {
  name: "BookingsList",
  title: "ridesListPage.documentTitle",
  components: {
    DataTable,
    RidesDriverFilter,
    RidesPassengerFilter,
    RidesServiceFilter,
  },
  data() {
    return {
      showFilters: true,
      fromDateMenu: false,
      toDateMenu: false,
      filterKey: 0,
      exporting: false,
      filters: {
        statuses: ["PAID"],
        captain_ids: [],
        passenger_ids: [],
        service_ids: [],
        time: null,
        from_date: companyToday(),
        to_date: companyToday(),
        order_id: null,
      },
    };
  },
  computed: {
    statusOptions() {
      return [
        { text: this.$t("ridesListPage.statusPending"), value: "PENDING" },
        { text: this.$t("ridesListPage.statusAccepted"), value: "ACCEPTED" },
        { text: this.$t("ridesListPage.statusStarted"), value: "STARTED" },
        { text: this.$t("ridesListPage.statusArrived"), value: "ARRIVED" },
        { text: this.$t("ridesListPage.statusPickedUp"), value: "PICKED_UP" },
        { text: this.$t("ridesListPage.statusDone"), value: "DONE" },
        { text: this.$t("ridesListPage.statusPaid"), value: "PAID" },
        { text: this.$t("ridesListPage.statusSystemCancelled"), value: "SYSTEM_CANCELLED" },
        { text: this.$t("ridesListPage.statusPassengerCancelled"), value: "PASSENGER_CANCELLED" },
        { text: this.$t("ridesListPage.statusCaptainCancelled"), value: "CAPTAIN_CANCELLED" },
        { text: this.$t("ridesListPage.statusCcCancelled"), value: "CC_CANCELLED" },
      ];
    },
    timeOptions() {
      return [
        { text: this.$t("ridesListPage.timeInstant"), value: "INSTANT" },
        { text: this.$t("ridesListPage.timeReservation"), value: "RESERVATION" },
      ];
    },
    searchParams() {
      const params = new URLSearchParams();

      // Add array filters - Laravel expects arrays without brackets
      if (this.filters.statuses && this.filters.statuses.length > 0) {
        this.filters.statuses.forEach((status) => {
          params.append("statuses[]", status);
        });
      }

      if (this.filters.captain_ids && this.filters.captain_ids.length > 0) {
        this.filters.captain_ids.forEach((id) => {
          params.append("captain_ids[]", id);
        });
      }

      if (this.filters.passenger_ids && this.filters.passenger_ids.length > 0) {
        this.filters.passenger_ids.forEach((id) => {
          params.append("passenger_ids[]", id);
        });
      }

      if (this.filters.service_ids && this.filters.service_ids.length > 0) {
        this.filters.service_ids.forEach((id) => {
          params.append("service_ids[]", id);
        });
      }

      // Add single value filters
      if (this.filters.time) {
        params.append("time", this.filters.time);
      }

      if (this.filters.from_date) {
        params.append("from_date", this.filters.from_date);
      }

      if (this.filters.to_date) {
        params.append("to_date", this.filters.to_date);
      }

      if (this.filters.order_id) {
        params.append("order_id", this.filters.order_id);
      }

      return params;
    },
    fieldsHidden() {
      // Hide all fields except the ones we want to show
      return [
        // 'datetime', // show datetime column
        'created_at',
        'updated_at',
        'deleted_at',
        'captain_id',
        'captain_first_name',
        'captain_last_name',
        'service_id',
        'taxi_number',
        'pickup_street',
        'dropdown_street',
        'car_plate_number',
        'car_brand_name',
        'car_brand_model',
        'car_color',
        'past_booking_rating',
        'ride_id',
        'pick_up_location_id',
        'pull_down_location_id',
        'now',
        'origin',
        'estimated_time',
        'estimated_distance',
        'estimated_price',
        'meter_km_count',
        'meter_waiting_time',
        'meter_elapsed_time',
        'meter_price',
        'captain_passenger_estimated_route',
        'summary',
        'user_id',
        'notes',
        'captain_eta'
      ];
    },
    titles() {
      return {
        id: this.$t("ridesListPage.colId"),
        pickup_name: this.$t("ridesListPage.colPickup"),
        dropdown_name: this.$t("ridesListPage.colDropoff"),
        captain_name: this.$t("ridesListPage.colDriverName"),
        passenger_name: this.$t("ridesListPage.colPassengerName"),
        service_name: this.$t("ridesListPage.colService"),
        status: this.$t("ridesListPage.status"),
        price: this.$t("ridesListPage.colPrice"),
        datetime: this.$t("ridesListPage.colDatetime"),
      };
    },
    actions() {
      return {
        details: {
          title: this.$t("ridesListPage.viewDetails"),
          icon: "info",
          action: (item) => {
            const route = this.$router.resolve({
              name: "Bookings",
              params: {
                id: item.id,
              },
            });
            window.open(route.href, "_blank");
          },
        },
      };
    },
    formatter() {
      const formatMoney = this.formatMoney;
      const currencyLabel = this.$t("ridesListPage.currency");
      const statusLabels = {
        PENDING: this.$t("ridesListPage.statusPending"),
        ACCEPTED: this.$t("ridesListPage.statusAccepted"),
        STARTED: this.$t("ridesListPage.statusStarted"),
        ARRIVED: this.$t("ridesListPage.statusArrived"),
        PICKED_UP: this.$t("ridesListPage.statusPickedUp"),
        DONE: this.$t("ridesListPage.statusDone"),
        PAID: this.$t("ridesListPage.statusPaid"),
        SYSTEM_CANCELLED: this.$t("ridesListPage.statusSystemCancelled"),
        PASSENGER_CANCELLED: this.$t("ridesListPage.statusPassengerCancelled"),
        CAPTAIN_CANCELLED: this.$t("ridesListPage.statusCaptainCancelled"),
        CC_CANCELLED: this.$t("ridesListPage.statusCcCancelled"),
      };
      return {
        status(value) {
          const statusColors = {
            PENDING: "#9f43cf",
            ACCEPTED: "#8e8e88",
            STARTED: "#428bca",
            ARRIVED: "#ffa60c",
            PICKED_UP: "#e04b4a",
            DONE: "#69b869",
            PAID: "#69b869",
            SYSTEM_CANCELLED: "#999",
            PASSENGER_CANCELLED: "#999",
            CAPTAIN_CANCELLED: "#999",
            CC_CANCELLED: "#999",
          };
          const label = statusLabels[value] || value;
          const color = statusColors[value] || "#000";
          return `<span style="color: ${color}; font-weight: bold;">${label}</span>`;
        },
        datetime(value) {
          if (!value) return "";
          return formatCompanyTime(value, "DD/MM/YYYY HH:mm");
        },
        price(value) {
          if (!value || value === 0) return "-";
          return `${formatMoney(value)} ${currencyLabel}`;
        },
      };
    },
  },
  methods: {
    applyFilters() {
      // Trigger DataTable refresh with new filters
      this.filterKey++;
      this.$nextTick(() => {
        if (this.$refs.ridesTable) {
          this.$refs.ridesTable.getData();
        }
      });
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = new URLSearchParams(this.searchParams);
        params.append("export", "csv");
        const response = await this.axios.get(
          `${this.api.ACTIONS.ADVANCED_SEARCH}?${params.toString()}`,
          { responseType: "blob" }
        );
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, response.data], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const fromDate = this.filters.from_date || "all";
        const toDate = this.filters.to_date || "all";
        link.setAttribute("download", `rides_${fromDate}_${toDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        this.exporting = false;
      }
    },
    resetFilters() {
      this.filters = {
        statuses: [],
        captain_ids: [],
        passenger_ids: [],
        service_ids: [],
        time: null,
        from_date: null,
        to_date: null,
        order_id: null,
        passenger: null,
        driver: null,
      };
      this.$nextTick(() => {
        this.applyFilters();
      });
    },
  },
};
</script>

<style scoped>
.filters-section {
  background-color: #f5f5f5;
}

.v-card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
